// import {WorkerQueryFilters} from "../components/Worker/WorkerFilterModal.tsx";
import { UserRole } from './settings';

// User model - simplified version for worker relationships
export interface User {
    id: number;
    username: string;
    keycloakId?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    role: UserRole;
}

export enum WorkerStatus {
    INCOMPLETE = "INCOMPLETE",
    // First-class automated states (owned by the backend state machine).
    PENDING_DOCUMENTS = "PENDING_DOCUMENTS",
    PENDING_APPROVAL = "PENDING_APPROVAL",
    CANDIDATE = "CANDIDATE",
    PLACED = "PLACED",
    REJECTED = "REJECTED",
    BLOCKED = "BLOCKED",
    RESERVE_POOL = "RESERVE_POOL",
    // Legacy filter alias — backend expands PENDING → [PENDING_DOCUMENTS, PENDING_APPROVAL].
    PENDING = "PENDING",
}

/** Worker table tab keys (enum-driven, not magic strings). */
export enum WorkerTabKey {
    INCOMPLETE = "incomplete",
    AWAITING_DOCS = "awaitingDocs",
    NEEDS_REVIEW = "needsReview",
    CANDIDATES = "candidates",
    PLACED = "placed",
    RESERVE_POOL = "reservePool",
    ALL = "all",
}

export enum WorkerAvailabilityStatus {
    AVAILABLE = "AVAILABLE",
    UNAVAILABLE = "UNAVAILABLE",
}

// ============= WORKER CERTIFICATIONS (new pipeline) =============
// Mirrors backend `WorkerCertificationsResponse` shape.

export enum CertReviewStatus {
    PENDING_EXTRACTION = 'PENDING_EXTRACTION',
    PENDING_REVIEW = 'PENDING_REVIEW',
    VERIFIED = 'VERIFIED',
    REJECTED = 'REJECTED',
    MANUAL_UNVERIFIED = 'MANUAL_UNVERIFIED',
    REUPLOAD_REQUIRED = 'REUPLOAD_REQUIRED',
}

export enum CertSourceChannel {
    WHATSAPP = 'WHATSAPP',
    WHATSAPP_DECLARATION = 'WHATSAPP_DECLARATION',
    DASHBOARD = 'DASHBOARD',
    MANUAL_ENTRY = 'MANUAL_ENTRY',
    OTHER = 'OTHER',
}

export enum CertCategory {
    SAFETY = 'SAFETY',
    WELDING = 'WELDING',
    EQUIPMENT = 'EQUIPMENT',
    ELECTRICAL = 'ELECTRICAL',
    MEDICAL = 'MEDICAL',
    LANGUAGE = 'LANGUAGE',
    OTHER = 'OTHER',
}

export interface ValidationFlag {
    code: string;
    detail?: string;
}

export interface WorkerCertification {
    id: string;
    workerId: number;
    // Populated by the review-queue endpoint (worker relation loaded). Used to
    // render a display label + a link to the worker's detail page.
    workerStringId?: string | null;
    workerPhoneNumber?: string | null;
    workerFirstName?: string | null;
    workerLastName?: string | null;
    proposedCertName?: string | null;
    certCategory?: CertCategory | null;
    issuer?: string | null;
    holderName?: string | null;
    holderDob?: string | null;
    certNumber?: string | null;
    issueDate?: string | null;
    expiryDate?: string | null;
    level?: string | null;
    sourceChannel: CertSourceChannel;
    // The worker_files row this cert was ingested from (Reassess path); null for
    // WhatsApp / direct-upload / manual certs. Lets the Files list tell whether a
    // certificate file already produced a (possibly VERIFIED) cert.
    workerFileId?: number | null;
    sourceFileUrl?: string | null;
    originalFileUrl?: string | null;
    aiConfidence?: Record<string, number> | null;
    aiPotentialIssues?: string[] | null;
    aiReasoning?: string | null;
    aiModelName?: string | null;
    validationFlags?: ValidationFlag[] | null;
    workerConfirmedAt?: string | null;
    workerCorrectionText?: string | null;
    inferredContext: boolean;
    status: CertReviewStatus;
    rejectionReason?: string | null;
    reviewedBy?: number | null;
    reviewedAt?: string | null;
    createdAt: string;
    updatedAt: string;
}

// Worker model.
/**
 * Multi-profession join row surfaced from the backend's `worker_professions`
 * table. The row with `isPrimary=true` mirrors `Worker.professionKey` for
 * backward compat — recruiter UI components should prefer iterating
 * `Worker.professions` when rendering chips.
 */
export interface WorkerProfession {
    professionKey: string;
    isActive: boolean;
    isPrimary: boolean;
    addedVia: 'PROFESSION_SELECTION' | 'ADD_PROFESSION_INTENT' | 'CV_DETECTED' | 'ADMIN' | string;
    customOccupation?: string | null;
    addedAt?: string | null;
    removedAt?: string | null;
}

/** Structured work-experience entry (mirrors backend WorkExperience). */
export interface WorkExperience {
    companyName: string;
    position: string;
    startDate: string;
    endDate?: string;
    isCurrentJob: boolean;
    workType?: string;
    responsibilities?: string;
}

/** Structured education entry (mirrors backend EducationEntry). */
export interface EducationEntry {
    educationType: string;
    institutionName: string;
    courseName: string;
    level?: string;
    startYear: number;
    endYear?: number;
    completed: boolean;
    diplomaReceived: boolean;
}

export type DesiredCompensationBasis = 'GROSS_HOURLY' | 'GROSS_MONTHLY' | 'ZZP_HOURLY';

export interface Worker {
    id: number;
    // Individual name fields
    firstName?: string | null;
    lastName?: string | null;
    middleName?: string | null;
    prefix?: string | null;
    // Computed fullName getter for backward compatibility
    fullName: string;
    workerId: string;
    dateOfBirth: string; // kept as a string for easier handling in forms
    emailAddress: string;
    phoneNumber: string;
    nationality: string;
    status: WorkerStatus;
    workerAvailability: WorkerAvailabilityStatus;
    availableAt?: string | null;  // Date when worker becomes available (for CANDIDATE status)
    languages?: string[];
    professionKey?: string;
    /** Multi-profession source of truth. Empty when backend hasn't seeded
     *  yet (legacy rows fall back to `professionKey`). */
    professions?: WorkerProfession[];
    customOccupation?: string | null;
    isCustomOccupation?: boolean;
    recruiterId?: number | null;
    recruiter?: User | null;
    accountManagerId?: number | null;
    accountManager?: User | null;
    memo?: string | null;
    isTestClient?: boolean;
    archived?: boolean;
    workAuthorization?: string | null;
    willingToWorkInNetherlands?: boolean | null;
    housingNeeded?: boolean | null;
    ownTransport?: boolean | null;
    desiredCompensationBasis?: DesiredCompensationBasis | null;
    desiredCompensationAmount?: number | null;
    workExperiences?: WorkExperience[] | null;
    education?: EducationEntry[] | null;
    createdAt: string;
    updatedAt: string;
    candidateReadiness?: WorkerCandidateReadiness;
    lifecycle?: WorkerLifecycle;
    // NAW doc fields — populated by the bot (chatbot flow) and by the recruiter
    // (worker detail page VCA/ID modals). Drive the expiry banner + NAW
    // readiness gate. All nullable.
    maskedBsn?: string | null;
    hasBsn?: boolean;
    idDocumentType?: 'PASSPORT' | 'ID_CARD' | 'RESIDENCE_PERMIT' | 'DRIVERS_LICENSE' | null;
    idDocumentNumber?: string | null;
    idDocumentValidFrom?: string | null;
    idDocumentValidUntil?: string | null;
    vcaNumber?: string | null;
    vcaType?: 'BASIS' | 'VOL' | 'VIL' | null;
    vcaValidFrom?: string | null;
    vcaValidUntil?: string | null;
    homeAddress?: {
        street: string | null;
        houseNumber?: string | null;
        postalCode: string | null;
        city: string | null;
        country: string | null;
    } | null;
    currentAddress?: {
        street: string | null;
        houseNumber?: string | null;
        postalCode: string | null;
        city: string | null;
        country: string | null;
    } | null;
    // Link to central-db. When set, WorkerDetail can drive the
    // EasyflexDocumentsTab + (in later stages) all other EasyFlex reads.
    easyflexRegistrationNumber?: string | null;
    easyflexOrganization?: string | null;
}

export interface WorkerLifecycle {
    cvCreated: boolean;
    offeredToClient: boolean;
    contacted: boolean;
}

export interface WorkerCandidateReadiness {
    isComplete: boolean;
    missingFields: string[];
    completedFields: string[];
}


export interface AddWorkerRequest{
    // Individual name fields
    firstName: string; // Required
    lastName: string; // Required
    middleName?: string;
    prefix?: string;
    // Backward compatibility
    fullName?: string;
    dateOfBirth?: string; // kept as a string for easier handling in forms
    emailAddress?: string;
    phoneNumber?: string; // Phone number is optional
    nationality?: string;
    professionKey: string; // Required profession key
    customOccupation?: string;
    isCustomOccupation?: boolean;
    status?: WorkerStatus;
    workerAvailability?: WorkerAvailabilityStatus;
    memo?: string;
    languages?: string[];
    recruiterId?: number;
    accountManagerId?: number;
    willingToWorkInNetherlands?: boolean | null;
    housingNeeded?: boolean | null;
    ownTransport?: boolean | null;
    desiredCompensationBasis?: DesiredCompensationBasis | null;
    desiredCompensationAmount?: number | null;
    homeAddress?: Worker['homeAddress'];
    // EasyFlex link — sent when importing from the EasyFlex tab.
    // Everything else EasyFlex-related is read live from central-db.
    easyflexRegistrationNumber?: string;
    easyflexOrganization?: string;
}

export interface WorkerSearchResponse {
    workers: Worker[];
    totalCount: number;
    totalPages: number;
}

// These are your domain filter types
export interface WorkerFilters {
    workerId?: string[];
    nationality?: string;
    proposedCertNames?: string[];
    status?: WorkerStatus;
    workerAvailability?: WorkerAvailabilityStatus;
    questionFilters?: QuestionFilter[];
}

// This is what your backend expects:
export interface WorkerSearchRequest extends Omit<WorkerFilters, 'status' | 'workerAvailability'> {
    page: number;
    limit: number;
    status?: string;       // as string
    searchParam?: string;
    workerAvailability?: string;   // as string
    accountManagerId?: number | null;
    recruiterId?: number | null;
    languages?: string[];
    nationalities?: string[];
    professions?: string[];
    isTestClient?: boolean;
    archived?: boolean;
    nonEuOnly?: boolean;
    rejectedBlockedOnly?: boolean;
    questionFilters?: QuestionFilter[];
    lastContactFrom?: string;
    lastContactTo?: string;
    residenceCountry?: string;
    residenceCity?: string;
    hasEndedPlacement?: boolean;
    minimumFeedbackRating?: number;
    maximumFeedbackRating?: number;
    easyflexAvailability?: EasyflexAvailabilityFilter;
    workState?: EasyflexWorkStateFilter;
    missingRequiredFeedback?: boolean;
    cvCreated?: boolean;
    offeredToClient?: boolean;
    contacted?: boolean;
}

export type EasyflexAvailabilityFilter = 'PLACED' | 'AVAILABLE' | 'INACTIVE';
export type EasyflexWorkStateFilter = 'WORKING' | 'PLANNED' | 'WORKING_OR_PLANNED' | 'NEITHER';

export interface WorkerQueryFilters {
    workerId?: string[];
    nationality?: string;
    nationalities?: string[]; // multiple nationalities filter
    proposedCertNames?: string[]; // free-text cert name filter (matches workerCertifications.proposedCertName VERIFIED + PENDING_REVIEW)
    status?: WorkerStatus;
    statuses?: WorkerStatus[]; // multiple statuses filter (for pending tab)
    workerAvailability?: WorkerAvailabilityStatus;
    languages?: string[]; // multiple languages filter
    professions?: string[]; // multiple professions filter
    profession?: string; // single profession filter for EasyFlex compatibility
    isIncomplete?: boolean; // filter for incomplete profiles
    hasActivePlacement?: boolean; // filter for active placement status (EasyFlex)
    isTestClient?: boolean; // filter for test/seed records
    archived?: boolean; // when true, return ONLY archived workers (default: exclude archived)
    nonEuOnly?: boolean; // when true, return ONLY non-EU workers (Non-EU toggle); default excludes them
    rejectedBlockedOnly?: boolean; // when true, return ONLY rejected/blocked workers; default excludes them from status-less lists
    accountManagerId?: number | null; // filter by account manager
    recruiterId?: number | null; // filter by recruiter
    page?: number;
    limit?: number;
    questionFilters?: QuestionFilter[];
    lastContactFrom?: string;
    lastContactTo?: string;
    residenceCountry?: string;
    residenceCity?: string;
    hasEndedPlacement?: boolean;
    minimumFeedbackRating?: number;
    maximumFeedbackRating?: number;
    easyflexAvailability?: EasyflexAvailabilityFilter;
    workState?: EasyflexWorkStateFilter;
    missingRequiredFeedback?: boolean;
    cvCreated?: boolean;
    offeredToClient?: boolean;
    contacted?: boolean;
}

export type AnswerType = 'BOOLEAN' | 'DATE' | 'RADIO' | 'MULTISELECT' | 'FILE' | 'TEXT' | 'NUMBER' | 'TEXT_AREA' | 'LIST_OBJECT';

export interface QuestionFilter {
    key: string;
    answerType: AnswerType;
    operator: string; // equals | contains | between | in | any
    value: any;       // string | boolean | number | string[] | {from?: string; to?: string}
    listField?: string;
}

export interface UpdateWorkerRequest {
    workerId: string,
    // Individual name fields (optional on update)
    firstName?: string | null;
    lastName?: string | null;
    middleName?: string | null;
    prefix?: string | null;
    fullName?: string;
    dateOfBirth?: string;
    emailAddress?: string;
    phoneNumber?: string;
    nationality?: string;
    // Professional info
    professionKey?: string | null;
    customOccupation?: string | null;
    isCustomOccupation?: boolean;
    languages?: string[];
    status?: WorkerStatus;
    workerAvailability?: WorkerAvailabilityStatus;
    availableAt?: string | null;  // Required when promoting to CANDIDATE
    recruiterId?: number | null;
    accountManagerId?: number | null;
    memo?: string | null;
    archived?: boolean;
    workAuthorization?: string | null;
    willingToWorkInNetherlands?: boolean | null;
    housingNeeded?: boolean | null;
    ownTransport?: boolean | null;
    desiredCompensationBasis?: DesiredCompensationBasis | null;
    desiredCompensationAmount?: number | null;
    // NAW doc fields — written by the bot (VCA/ID upload + confirmation
    // handlers) and by the recruiter (VcaConfirmationModal / IdConfirmationModal
    // on the worker detail page).
    bsn?: string | null;
    idDocumentType?: 'PASSPORT' | 'ID_CARD' | 'RESIDENCE_PERMIT' | 'DRIVERS_LICENSE' | null;
    idDocumentNumber?: string | null;
    idDocumentValidFrom?: string | null;
    idDocumentValidUntil?: string | null;
    vcaNumber?: string | null;
    vcaType?: 'BASIS' | 'VOL' | 'VIL' | null;
    vcaValidFrom?: string | null;
    vcaValidUntil?: string | null;
    homeAddress?: {
        street: string | null;
        houseNumber?: string | null;
        postalCode: string | null;
        city: string | null;
        country: string | null;
    } | null;
    currentAddress?: {
        street: string | null;
        houseNumber?: string | null;
        postalCode: string | null;
        city: string | null;
        country: string | null;
    } | null;
}

export interface UpdateWorkerDto {
    // Individual name fields
    firstName?: string;
    lastName?: string;
    middleName?: string;
    prefix?: string;
    // Backward compatibility
    fullName?: string;
    dateOfBirth?: string;
    emailAddress?: string;
    phoneNumber?: string;
    nationality?: string;
    status?: WorkerStatus;
    workerAvailability?: WorkerAvailabilityStatus;
    availableAt?: string | null;  // Required when promoting to CANDIDATE
    recruiterId?: number | null;
    accountManagerId?: number | null;
    memo?: string | null;
}

export interface BulkUpdateWorkersRequest{
    workerIds: string[];
    updates: UpdateWorkerRequest;
}

export interface BulkUpdateWorkersDto {
    workerIds: string[];
    updates: UpdateWorkerDto;
}

export interface BulkDeleteWorkersDto {
    workerIds: string[];
}

export function mapWorkerQueryFiltersToSearchRequest(
    filters: WorkerQueryFilters,
): Partial<WorkerSearchRequest> {
    const {
        workerId,
        nationality,
        nationalities,
        proposedCertNames,
        status,
        statuses,
        workerAvailability,
        languages,
        professions,
        accountManagerId,
        recruiterId,
    } = filters;

    const result: Partial<WorkerSearchRequest> = {};

    if (workerId) result.workerId = workerId;
    if (nationality) result.nationality = nationality;
    if (nationalities && nationalities.length > 0) result.nationalities = nationalities;
    if (proposedCertNames && proposedCertNames.length > 0) {
        result.proposedCertNames = proposedCertNames;
    }

    if (statuses && statuses.length > 0) {
        result.status = statuses[0].toString();
    } else if (status) {
        result.status = status.toString();
    }

    if (workerAvailability) {
        result.workerAvailability = workerAvailability.toString();
    }
    if (languages && languages.length > 0) result.languages = languages;
    if (professions && professions.length > 0) result.professions = professions;
    if (filters.archived !== undefined) result.archived = filters.archived;
    if (filters.nonEuOnly !== undefined) result.nonEuOnly = filters.nonEuOnly;
    if (filters.rejectedBlockedOnly !== undefined) result.rejectedBlockedOnly = filters.rejectedBlockedOnly;
    if (accountManagerId !== undefined) result.accountManagerId = accountManagerId;
    if (recruiterId !== undefined) result.recruiterId = recruiterId;

    if (filters.questionFilters && filters.questionFilters.length > 0) {
        result.questionFilters = filters.questionFilters;
    }
    if (filters.lastContactFrom) result.lastContactFrom = filters.lastContactFrom;
    if (filters.lastContactTo) result.lastContactTo = filters.lastContactTo;
    if (filters.residenceCountry) result.residenceCountry = filters.residenceCountry;
    if (filters.residenceCity) result.residenceCity = filters.residenceCity;
    if (filters.hasEndedPlacement !== undefined) result.hasEndedPlacement = filters.hasEndedPlacement;
    if (filters.minimumFeedbackRating !== undefined) result.minimumFeedbackRating = filters.minimumFeedbackRating;
    if (filters.maximumFeedbackRating !== undefined) result.maximumFeedbackRating = filters.maximumFeedbackRating;
    if (filters.easyflexAvailability) result.easyflexAvailability = filters.easyflexAvailability;
    if (filters.workState) result.workState = filters.workState;
    if (filters.missingRequiredFeedback !== undefined) result.missingRequiredFeedback = filters.missingRequiredFeedback;
    if (filters.cvCreated !== undefined) result.cvCreated = filters.cvCreated;
    if (filters.offeredToClient !== undefined) result.offeredToClient = filters.offeredToClient;
    if (filters.contacted !== undefined) result.contacted = filters.contacted;

    return result;
}
